import { computed, ref, watch } from "vue";
import { createInsuranceFilterApi } from "#ce/shared/filters/insurance-plan/api/insurance-filter";

const root = "root";

export const useInsuranceFilterStore = (options = {}) => {
  const api = options.filterApi ?? createInsuranceFilterApi(options);
  const meta = ref(undefined);

  const loading = ref(false);
  const error = ref(null);

  const modelLookupMap = ref(new Map());

  // Mirror of the container's focused item, synced via @update-current-item.
  // Null when the container is at root.
  const currentItem = ref(null);
  const hasParent = computed(() => currentItem.value !== null);

  // Items handed to the container as its root frame.
  // - Without a search query: children of `currentItem` (or root carriers when null).
  // - With a search query: leaf-search results from the subtree rooted at `currentItem`.
  const models = ref([]);

  const selectedModel = ref(undefined);

  const searchQuery = ref("");
  const sanitizedSearchQuery = computed(() => searchQuery.value.trim().toLowerCase());
  const hasSearchQuery = computed(() => sanitizedSearchQuery.value.length > 0);

  watch(searchQuery, recomputeModels);
  watch(currentItem, recomputeModels);

  async function load(selected = undefined) {
    loading.value = true;
    // Clear any error from a previous load so a recovered fetch isn't treated as still-failed.
    // Consumers gate on `error` (the filter's self-heal, the modal's plan validation), so a
    // stale error from an earlier failure would keep them disabled even after a successful retry.
    error.value = null;

    try {
      const response = await api.all();

      meta.value = response.meta;
      modelLookupMap.value = new Map(response.data.map((entry) => [entry.value, entry]));

      const initial = selected ? findByValue(selected) : undefined;
      selectedModel.value = initial;
      recomputeModels();
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
  }

  function findByValue(value) {
    return modelLookupMap.value.get(value);
  }

  function setCurrentItem(item) {
    currentItem.value = item ?? null;
  }

  function resetCurrentItem() {
    currentItem.value = null;
  }

  function getChildrenOf(item) {
    const key = item?.value ?? root;
    return modelsGroupedByParent.value.get(key) ?? [];
  }

  // Values that some other plan points to as its `parent` — i.e. group/LOB records that exist only to
  // hold children. They render as display-only headers, never as selectable options.
  const referencedParents = computed(
    () =>
      new Set(
        Array.from(modelLookupMap.value.values())
          .map((model) => model.parent)
          .filter(Boolean),
      ),
  );

  // A slug is selectable only if it's a known *leaf* plan. Group parents are display-only, so a parent
  // slug (e.g. `group-employer-plans`) must never count as a chosen plan — used by the required-filter
  // validation so a parent slug in the URL can't satisfy the requirement and skip the modal.
  function isSelectable(value) {
    return modelLookupMap.value.has(value) && !referencedParents.value.has(value);
  }

  const modelsGroupedByParent = computed(() => {
    const groups = new Map();

    for (const model of modelLookupMap.value.values()) {
      const key = model.parent ?? root;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      const entry = groups.get(key);
      entry.push({ ...model, hasChildren: referencedParents.value.has(model.value) });
    }

    return groups;
  });

  // Stable root-level grouped list for the grouped radio-card (permanent)
  // presentation. Unlike `models` — which the drilldown recomputes as the focused
  // frame changes — this is derived directly from the full lookup map, so it stays
  // constant regardless of selection. Each top-level parent carries its
  // `description` (the group's subheading) and its child plans inline. The shape
  // is purely structural; presentation is applied by the consuming component.
  //
  // Groups are ordered by their server-assigned `position` (a curated order set on
  // the parent records), falling back to the order the API returned them in for any
  // group without a position.
  const groupedRootModels = computed(() => {
    const roots = [...(modelsGroupedByParent.value.get(root) ?? [])];

    return roots
      .map((group, index) => ({ group, index }))
      .sort((a, b) => positionOf(a.group) - positionOf(b.group) || a.index - b.index)
      .map(({ group }) => {
        if (!group.hasChildren) {
          return group;
        }
        const children = modelsGroupedByParent.value.get(group.value) ?? [];
        return {
          ...group,
          children,
        };
      });
  });

  // Numeric sort key for a group's curated position. Groups without a position sort
  // last (after every positioned group) while keeping their original relative order.
  function positionOf(group) {
    return Number.isFinite(group?.position) ? group.position : Number.MAX_SAFE_INTEGER;
  }

  function collectLeafDescendants(parentValue) {
    const leaves = [];
    const walk = (parent) => {
      const children = modelsGroupedByParent.value.get(parent) ?? [];
      for (const child of children) {
        if (child.hasChildren) {
          walk(child.value);
        } else {
          leaves.push(child);
        }
      }
    };
    walk(parentValue);
    return leaves;
  }

  function recomputeModels() {
    const scopeValue = currentItem.value?.value ?? root;

    if (!hasSearchQuery.value) {
      models.value = getChildrenOf(currentItem.value);
      return;
    }

    const result = collectLeafDescendants(scopeValue).filter(({ text }) =>
      text.toLowerCase().includes(sanitizedSearchQuery.value),
    );

    const uniqueParents = new Set(result.map(({ parent }) => parent));
    if (uniqueParents.size > 1) {
      models.value = result.map((model) => {
        const parent = findByValue(model.parent);
        const group = meta.value?.search?.[parent ? "leaves" : "root"];
        const subtitle = parent?.text;
        return { ...model, subtitle, group };
      });
    } else {
      models.value = result;
    }
  }

  function computeDepth(item) {
    if (!item) {
      return 0;
    }
    let depth = 1;
    let parentValue = item.parent;
    while (parentValue && parentValue !== root) {
      depth++;
      const parent = modelLookupMap.value.get(parentValue);
      if (!parent) {
        break;
      }
      parentValue = parent.parent;
    }
    return depth;
  }

  const currentDepth = computed(() => computeDepth(currentItem.value));

  const stepTitle = computed(() => {
    if (hasSearchQuery.value) {
      return "Search";
    }
    return meta.value?.steps?.[currentDepth.value]?.text ?? "";
  });

  // Ancestor chain of `model`, top-most first, excluding the synthetic `root`
  // and `model` itself. e.g. plan -> aetna -> root yields [aetnaItem]; plan ->
  // carrier -> region -> root yields [regionItem, carrierItem].
  function ancestorPath(model) {
    if (!model) {
      return [];
    }
    const path = [];
    let parentValue = model.parent;
    while (parentValue && parentValue !== root) {
      const parent = findByValue(parentValue);
      if (!parent) {
        break;
      }
      path.unshift(parent);
      parentValue = parent.parent;
    }
    return path;
  }

  // Drill path the container should seed into when the picker opens. Empty while
  // a search query is active so the remount on the search active/inactive
  // transition doesn't re-seed to the carrier instead of showing search results.
  const initialPath = computed(() => (hasSearchQuery.value ? [] : ancestorPath(selectedModel.value)));

  const progress = computed(() => {
    if (selectedModel.value) {
      return 100;
    }

    // parentIndex = depth - 1, with a 10% floor for the root and first
    // drill. Depth 2 -> 50%, depth 3 -> 66.66%, depth 4 -> 75%.
    const depth = currentDepth.value;
    if (depth <= 1) {
      return 10;
    }
    const parentIndex = depth - 1;
    return parentIndex * (100 / (parentIndex + 1));
  });

  return {
    loading,
    error,
    stepTitle,
    models,
    groupedRootModels,
    selectedModel,
    hasParent,
    progress,
    currentItem,
    searchQuery,
    hasSearchQuery,
    initialPath,

    load,
    findByValue,
    isSelectable,
    setCurrentItem,
    resetCurrentItem,
    getChildrenOf,
  };
};
